import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    Avatar,
    Stack,
    Switch,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LogoutIcon from '@mui/icons-material/Logout';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { sk, enUS, cs } from 'date-fns/locale';

import automationService from '../services/automation.service';
import AutomationRuleSettings from './AutomationRuleSettings';
import AutomationHistoryDrawer from './AutomationHistoryDrawer';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';
import {
    AUTOMATION_TRIGGER_TYPES,
    getDefaultRuleData,
    buildRulePayload,
    mapRulesByTriggerType,
    parseRuleToFormState,
} from '../config/automationConfig';

const DATE_FNS_LOCALE = { SK: sk, EN: enUS, CZ: cs };

const TRIGGER_ICONS = {
    long_break: FreeBreakfastIcon,
    long_shift_reminder: ScheduleIcon,
    auto_end_shift: LogoutIcon,
};

const AutomationRuleRow = ({
    triggerType,
    rule,
    toggling,
    onToggle,
    onEnable,
    onRuleUpdated,
    t,
    language,
    theme,
}) => {
    const [expandedPanel, setExpandedPanel] = useState(false);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [lastExecutedAt, setLastExecutedAt] = useState(null);
    const Icon = TRIGGER_ICONS[triggerType] || AutoModeIcon;
    const exists = Boolean(rule?.id);
    const isActive = exists && rule.is_active;

    const handleSettingsSaved = (payload) => {
        onRuleUpdated({ ...rule, ...payload });
    };

    const lastRunLabel = () => {
        if (!exists || !lastExecutedAt) {
            return `${t('pages.automations.lastRun')}: ${t('pages.automations.neverRun')}`;
        }
        const locale = DATE_FNS_LOCALE[language] || sk;
        return `${t('pages.automations.lastRun')}: ${formatDistanceToNow(parseISO(lastExecutedAt), { addSuffix: true, locale })}`;
    };

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 4,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(0,0,0,0.25)'
                    : '0 4px 12px rgba(0,0,0,0.04)',
            }}
        >
            <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 44, height: 44 }}>
                    <Icon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {t(`pages.automations.types.${triggerType}.title`)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {t(`pages.automations.types.${triggerType}.description`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {lastRunLabel()}
                    </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, pt: 0.5 }}>
                    {exists ? (
                        <Switch
                            checked={isActive}
                            disabled={toggling}
                            onChange={() => onToggle(rule)}
                            inputProps={{ 'aria-label': t(`pages.automations.types.${triggerType}.title`) }}
                        />
                    ) : (
                        <Button
                            variant="contained"
                            size="small"
                            disabled={toggling}
                            onClick={() => onEnable(triggerType)}
                            sx={{ borderRadius: '12px', boxShadow: 'none' }}
                        >
                            {t('pages.automations.enable')}
                        </Button>
                    )}
                </Box>
            </Stack>

            {exists && (
                <Box sx={{ mt: 2 }}>
                    <Accordion
                        expanded={expandedPanel}
                        onChange={(_, expanded) => setExpandedPanel(expanded)}
                        disableGutters
                        elevation={0}
                        sx={{
                            bgcolor: 'transparent',
                            '&:before': { display: 'none' },
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2" fontWeight={600}>
                                {t('pages.automations.settings')}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <AutomationRuleSettings rule={rule} onSaved={handleSettingsSaved} />
                        </AccordionDetails>
                    </Accordion>

                    <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setHistoryDrawerOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setHistoryDrawerOpen(true);
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            py: 1.5,
                            px: 2,
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'action.hover',
                            },
                        }}
                    >
                        <Typography variant="body2" fontWeight={600}>
                            {t('pages.automations.history')}
                        </Typography>
                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </Box>

                    <AutomationHistoryDrawer
                        open={historyDrawerOpen}
                        onClose={() => setHistoryDrawerOpen(false)}
                        rule={rule}
                        triggerType={triggerType}
                        onLastRunLoaded={setLastExecutedAt}
                    />
                </Box>
            )}
        </Paper>
    );
};

const AutomationManagement = () => {
    const theme = useTheme();
    const { t, language, isDarkMode } = usePreferences();
    const shellColors = getThemeColors(isDarkMode);
    const [rulesByType, setRulesByType] = useState({});
    const [loading, setLoading] = useState(true);
    const [togglingType, setTogglingType] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const rulesResponse = await automationService.getRules();
            setRulesByType(mapRulesByTriggerType(rulesResponse.data || []));
        } catch (error) {
            console.error('Error fetching automation rules', error);
            setSnackbar({ open: true, message: t('pages.automations.fetchError'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const handleRuleUpdated = (updatedRule) => {
        setRulesByType((prev) => ({
            ...prev,
            [updatedRule.trigger_type]: { ...prev[updatedRule.trigger_type], ...updatedRule },
        }));
        setSnackbar({ open: true, message: t('pages.automations.saved'), severity: 'success' });
    };

    const handleToggle = async (rule) => {
        setTogglingType(rule.trigger_type);
        const newActive = !rule.is_active;
        try {
            const form = parseRuleToFormState(rule);
            const payload = buildRulePayload({ ...rule, is_active: newActive }, form);
            payload.is_active = newActive;
            await automationService.updateRule(rule.id, payload);
            setRulesByType((prev) => ({
                ...prev,
                [rule.trigger_type]: { ...rule, is_active: newActive },
            }));
        } catch (error) {
            console.error('Failed to toggle rule', error);
            setSnackbar({ open: true, message: t('pages.automations.toggleError'), severity: 'error' });
        } finally {
            setTogglingType(null);
        }
    };

    const handleEnable = async (triggerType) => {
        setTogglingType(triggerType);
        try {
            const name = t(`pages.automations.types.${triggerType}.title`);
            const payload = getDefaultRuleData(triggerType, name);
            const response = await automationService.createRule(payload);
            const created = response.data;
            setRulesByType((prev) => ({ ...prev, [triggerType]: created }));
            setSnackbar({ open: true, message: t('pages.automations.enabled'), severity: 'success' });
        } catch (error) {
            console.error('Failed to enable automation', error);
            setSnackbar({ open: true, message: t('pages.automations.toggleError'), severity: 'error' });
        } finally {
            setTogglingType(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    px: { xs: 2, sm: 3 },
                    pt: { xs: 2, sm: 3 },
                    pb: 2,
                    bgcolor: shellColors.shellBackground || shellColors.background || '#F3F4F6',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        backgroundImage: 'none',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: isDarkMode
                            ? '0 8px 24px rgba(0, 0, 0, 0.35)'
                            : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {t('pages.automations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {t('pages.automations.description')}
                    </Typography>
                </Paper>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {AUTOMATION_TRIGGER_TYPES.map((triggerType) => (
                    <AutomationRuleRow
                        key={triggerType}
                        triggerType={triggerType}
                        rule={rulesByType[triggerType]}
                        toggling={togglingType === triggerType}
                        onToggle={handleToggle}
                        onEnable={handleEnable}
                        onRuleUpdated={handleRuleUpdated}
                        t={t}
                        language={language}
                        theme={theme}
                    />
                ))}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AutomationManagement;
